// @flow
import { colors } from 'bench-style-constants';
import React from 'react';
import styled from 'styled-components';
import { iconPaths } from './Icon';

export const checkboxStates = {
  CHECKED: 'CHECKED',
  INDETERMINATE: 'INDETERMINATE',
  UNCHECKED: 'UNCHECKED',
};

const BaseCheckbox = styled.span`
  border: 1px solid;
  border-radius: 2px;
  box-sizing: border-box;
  display: inline-block;
  height: 16px;
  position: relative;
  transition-duration: 200ms;
  width: 16px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

const checkIconURL = encodeURIComponent(`
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path fill="${colors.white}" d="${iconPaths.check}" />
  </svg>
`);

const CheckedCheckbox = styled(BaseCheckbox)`
  border-color: ${({ disabled }) => (disabled ? colors.shell : colors.water)};
  background: ${({ disabled }) =>
    disabled
      ? `${colors.stone} url('data:image/svg+xml;utf8,${checkIconURL}') 50% 50% no-repeat`
      : `${colors.water} url('data:image/svg+xml;utf8,${checkIconURL}') 50% 50% no-repeat`};
  background-size: 24px;

  &:hover {
    border-color: ${({ disabled }) => (disabled ? colors.shell : colors.creek)};
    border-color: ${({ disabled }) => (disabled ? colors.shell : colors.creek)};
  }
`;

const IndeterminateCheckbox = styled(BaseCheckbox)`
  background: ${({ disabled }) => (disabled ? colors.stone : colors.water)};
  border-color: ${({ disabled }) => (disabled ? colors.white : colors.water)};

  // This ::after pseudo-element produces the minus icon; this looks much nicer than scaling the
  // SVG minus icon to fit in the checkbox
  &::after {
    background: ${colors.white};
    content: '';
    display: block;
    height: 2px;
    left: 2px;
    position: absolute;
    top: 6px;
    width: 10px;
  }

  &:hover {
    background: ${({ disabled }) => (disabled ? colors.stone : colors.creek)};
    border-color: ${({ disabled }) => (disabled ? colors.white : colors.creek)};
  }
`;

const UncheckedCheckbox = styled(BaseCheckbox)`
  background: ${colors.white};
  border-color: ${({ disabled }) => (disabled ? colors.silt : colors.stone)};

  &:hover {
    border-color: ${({ disabled }) => (disabled ? colors.shell : colors.water)};
  }
`;

const statesToComponents = {
  [checkboxStates.CHECKED]: CheckedCheckbox,
  [checkboxStates.INDETERMINATE]: IndeterminateCheckbox,
  [checkboxStates.UNCHECKED]: UncheckedCheckbox,
};

type CheckboxProps = {
  checkboxState: $Values<typeof checkboxStates>,
  disabled?: boolean,
  onClick?: any => void,
};

const Checkbox = ({ checkboxState, disabled, onClick, ...domAttrs }: CheckboxProps) => {
  const CheckboxComponent = statesToComponents[checkboxState];
  return (
    <CheckboxComponent
      disabled={disabled}
      onClick={event => {
        if (!disabled && typeof onClick !== 'undefined') {
          return onClick(event);
        }
      }}
      {...domAttrs}
    />
  );
};

Checkbox.defaultProps = {
  disabled: false,
};

export default Checkbox;
